/** API of the users referential (port of UserService). */
import { queryOptions } from '@tanstack/react-query';
import { z } from 'zod';
import { api } from '@/shared/api/client';
import type { WithId } from '@/shared/api/types';

export const userRecordSchema = z.object({
  id: z.string(),
  username: z.string().nullish(),
  email: z.string().nullish(),
  language: z.string().nullish(),
  role: z.string().nullish(),
  disabled: z.boolean().nullish(),
  abcdiorAccess: z.boolean().nullish(),
  dmsAccess: z.boolean().nullish(),
  dmsSettings: z
    .object({
      measurements: z.string().nullish(),
      villaDior: z.boolean().nullish(),
    })
    .partial()
    .nullish(),
  assignedStores: z.array(z.string()).nullish(),
  workshopIds: z.array(z.string()).nullish(),
});

export type UserRecord = z.infer<typeof userRecordSchema>;

export const usersQueryOptions = () =>
  queryOptions({
    queryKey: ['users'] as const,
    queryFn: () =>
      api.get<UserRecord[]>('user', { schema: z.array(userRecordSchema) }),
    retry: false,
  });

/** Payload of the create / update calls. */
export interface UserInput {
  username: string;
  email: string;
  language: string;
  role: string;
  disabled: boolean;
  abcdiorAccess: boolean;
  dmsAccess: boolean;
  measurements: string | null;
  villaDior: boolean;
  workshopIds: string[];
  dmsSettings: { measurements: string | null; villaDior: boolean };
  assignedStores: string[];
}

export const createUser = (data: UserInput) => api.post<unknown>('user', data);

export const updateUser = (data: WithId<UserInput>) =>
  api.put<unknown>('user', data);

/** `PATCH user/:id/villa-dior/activate|deactivate`: the Villa Dior flag of the MTM settings. */
export const setVillaDior = (id: string, active: boolean) =>
  api.patch<unknown>(
    `user/${encodeURIComponent(id)}/villa-dior/${active ? 'activate' : 'deactivate'}`,
    {},
  );
